import React from 'react';
import type { Video } from '../../types/video.types';
import SearchDiscoveryPanels, { DiscoveryTag, SearchDiscoverySkeleton } from './SearchDiscoveryPanels';
import styles from './SearchDiscoveryContent.module.scss';

type RailBreakpoint = 'none' | '1280' | '1440';

interface SearchDiscoveryContentProps {
    tags: DiscoveryTag[];
    fighters: string[];
    events: string[];
    videos?: Video[];
    isLoading?: boolean;
    isError?: boolean;
    isBelowMinimum?: boolean;
    showVideos?: boolean;
    showViewAll?: boolean;
    showNoResultsMessage?: boolean;
    railBreakpoint?: RailBreakpoint;
    mobileMetaFirst?: boolean;
    className?: string;
    emptyMessage?: string;
    belowMinimumMessage?: string;
    noResultsMessage?: string;
    errorTitle?: string;
    errorCopy?: string;
    errorActionLabel?: string;
    onVideoSelect?: (video: Video) => void;
    onTagSelect?: (tag: DiscoveryTag) => void;
    onFighterSelect?: (fighter: string) => void;
    onEventSelect?: (eventName: string) => void;
    onViewAll?: () => void;
}

const hasResults = (
    videos: Video[],
    tags: DiscoveryTag[],
    fighters: string[],
    events: string[],
    showVideos: boolean
) => (
    (showVideos && videos.length > 0)
    || tags.length > 0
    || fighters.length > 0
    || events.length > 0
);

/**
 * Shared discovery state and panel renderer without an embedded search input.
 */
const SearchDiscoveryContent = ({
    tags,
    fighters,
    events,
    videos = [],
    isLoading = false,
    isError = false,
    isBelowMinimum = false,
    showVideos = false,
    showViewAll = false,
    showNoResultsMessage = false,
    railBreakpoint = 'none',
    mobileMetaFirst = false,
    className,
    emptyMessage = 'Explore popular tags or search for a fighter, event, or video.',
    belowMinimumMessage = 'Keep typing to reveal matching tags, fighters, and events.',
    noResultsMessage = 'No matching tags, fighters, or events yet.',
    errorTitle = 'Search suggestions are unavailable right now.',
    errorCopy,
    errorActionLabel,
    onVideoSelect,
    onTagSelect,
    onFighterSelect,
    onEventSelect,
    onViewAll
}: SearchDiscoveryContentProps) => {
    const hasAnyResults = hasResults(videos, tags, fighters, events, showVideos);
    const contentClassName = [
        styles.content,
        className ?? ''
    ].filter(Boolean).join(' ');

    return (
        <div className={contentClassName}>
            {isLoading ? (
                <SearchDiscoverySkeleton />
            ) : isError ? (
                <div className={styles.errorState}>
                    <p className={styles.errorTitle}>{errorTitle}</p>
                    {errorCopy ? <p className={styles.errorCopy}>{errorCopy}</p> : null}
                    {errorActionLabel && onViewAll ? (
                        <button type="button" className={styles.viewAllButton} onClick={onViewAll}>
                            {errorActionLabel}
                        </button>
                    ) : null}
                </div>
            ) : isBelowMinimum ? (
                <div className={styles.stateMessage}>
                    {belowMinimumMessage}
                </div>
            ) : hasAnyResults ? (
                <SearchDiscoveryPanels
                    videos={videos}
                    tags={tags}
                    fighters={fighters}
                    events={events}
                    showVideos={showVideos}
                    showViewAll={showViewAll}
                    onVideoSelect={onVideoSelect}
                    onTagSelect={onTagSelect}
                    onFighterSelect={onFighterSelect}
                    onEventSelect={onEventSelect}
                    onViewAll={onViewAll}
                    railBreakpoint={railBreakpoint}
                    mobileMetaFirst={mobileMetaFirst}
                />
            ) : (
                <div className={styles.stateMessage}>
                    {showNoResultsMessage ? noResultsMessage : emptyMessage}
                </div>
            )}
        </div>
    );
};

export default SearchDiscoveryContent;
